import React from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import {
  NOT_PROVIDED, NOT_SAVED, NOT_SENT, NOT_YET, isUnreadable, readRequestDetails, stateLine, valueRepeatsState,
} from './requestData';
import { inputIndicator } from './checkEvidence';
import { checksNotSavedText } from './checkDisplay';

/**
 * ISFR's Request Details tab: what the partner submitted, each input with the
 * state the agent's checks found it in (readable, an issue, missing, not
 * required), and the request's operational fields as APFP sent them. Lets a
 * reviewer check the inputs behind a finding without opening the portal.
 */

const muted = (content) => <Box color="text-body-secondary">{content}</Box>;

const display = (value) => {
  if (value === NOT_SENT) return muted('Not in the APFP payload');
  if (value === NOT_PROVIDED) return muted('Not provided');
  if (value === NOT_SAVED) return muted('Not saved for this request');
  if (value === NOT_YET) return muted('Not available: the data source is not connected yet');
  // Sent, but in a shape this view does not recognise: shown raw so the
  // reviewer sees what arrived instead of a guess.
  if (isUnreadable(value)) {
    return <Box color="text-status-warning">{`Sent in an unrecognised format: ${JSON.stringify(value.unreadable)}`}</Box>;
  }
  return <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</span>;
};

// Each row: its label, the short colored status, then the value, any detail and the
// explanation, each on its own line, so the statuses line up down the page.
const Item = ({ item }) => {
  const line = item.state ? stateLine(item.state) : null;
  return (
    <div data-testid="isfr-request-input">
      <Box variant="awsui-key-label">{item.label}</Box>
      {line && (
        <StatusIndicator type={inputIndicator(item.state.kind, item.state.enforced !== false)}>{line.short}</StatusIndicator>
      )}
      {!valueRepeatsState(item) && <div>{display(item.value)}</div>}
      {item.detail && <Box variant="small" display="block" color="text-body-secondary">{item.detail}</Box>}
      {line && line.note && <Box variant="small" display="block"><span style={{ whiteSpace: 'pre-wrap' }}>{line.note}</span></Box>}
    </div>
  );
};

const RequestDetails = ({ request }) => {
  const details = readRequestDetails(request);
  if (details.status !== 'ok') {
    return (
      <Container header={<Header variant="h2">Request Details</Header>}>
        {muted('This record stores no request data, so there is nothing to show here.')}
      </Container>
    );
  }
  return (
    <SpaceBetween size="l">
      {details.checksStatus !== 'ok' && (
        <Box color="text-body-secondary">
          {`${checksNotSavedText(details.checksStatus)} Until then, inputs show only what APFP sent, with no state.`}
        </Box>
      )}
      {details.groups.map((group) => (
        <Container
          key={group.title}
          header={<Header variant="h2" description={group.description}>{group.title}</Header>}
        >
          <ColumnLayout columns={3} variant="text-grid">
            {group.items.map((item) => <Item key={item.label} item={item} />)}
          </ColumnLayout>
        </Container>
      ))}
      <Container
        header={(
          <Header
            variant="h2"
            description="Operational fields only. Free text and contact details are not stored on the record."
          >
            Stored request data
          </Header>
        )}
      >
        <ExpandableSection headerText="Show as JSON">
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0, fontSize: '12px' }}>
            {JSON.stringify(details.fundRequest, null, 2)}
          </pre>
        </ExpandableSection>
      </Container>
    </SpaceBetween>
  );
};

export default RequestDetails;
